import { createLayer } from "@/api/hooks/layer";
import type { LayerUnion, LayerUnionWithAll } from "@/layers";
import type { WeatherTableState } from "@/reducer/WeatherTablesState";
import type { Plot, PlotSeriesType } from "@/reducer/client-models";
import type {
  CartographicMapOutput,
  Profile,
  SlimProfile,
  Tab,
  TabBase,
  TephigramSchemaOutput,
} from "@mm/metx-workbench.meteomatics.com";
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";

type ProfileElement = {
  type: "profile";
  item: Profile | SlimProfile;
};
type TabElement = {
  type: "tab";
  item: Tab | TabBase;
};
type LayerElement = {
  type: "layer";
  tabId: Tab["id"];
  item: LayerUnionWithAll;
};
type PlotSeriesElement = {
  type: "plot-serie";
  plotSerieIdx: PlotSeriesType["index"];
  item: Plot;
};
type TableColumnElement = {
  type: "table-column";
  profileId: Profile["id"];
  item: WeatherTableState;
  groupIdx: number;
  columnIdx: number;
};
type RemoveWindowLabel = {
  /**
   * A text label to show in the delete modal.
   */
  label: string;
};

export type RemovableElement =
  | (ProfileElement & RemoveWindowLabel)
  | (TabElement & RemoveWindowLabel)
  | (LayerElement & RemoveWindowLabel)
  | (PlotSeriesElement & RemoveWindowLabel)
  | (TableColumnElement & RemoveWindowLabel)
  | null;

type UiState = {
  selectedLayerId: LayerUnion["id"] | null;
  selectedElementToRemove: RemovableElement;
  headerVisibilityMode: "ALWAYS_SHOW" | "TOGGLABLE_ON_HOVER";
  footerVisibilityMode: "ALWAYS_SHOW" | "TOGGLABLE_ON_HOVER";
  selectedMap: CartographicMapOutput | null;
  selectedPlot: Plot | null;
  selectedTool: Plot | WeatherTableState | TephigramSchemaOutput | null; //TODO create Tool type? to combine Plot and table and more
  selectedPlotSerieForEditParameter: PlotSeriesType | null;
  selectedLayerForAddParameter: LayerUnionWithAll | null;
  selectedLayerForEditParameter: LayerUnionWithAll | null;
  isLayoutArrangementActive: boolean;
  isQuickSelectActive: boolean;
  sideNavFixed: boolean;
  sideNavOpen: boolean;
  isInteractionLocked: boolean;
  aiChatIsRecording: boolean;
  aiChatIsProcessing: boolean;
};

const GLOBAL_QUICK_SELECT_KEY = "global-quick-select";

const initialQuickSelectValue: boolean = localStorage.getItem(GLOBAL_QUICK_SELECT_KEY)
  ? JSON.parse(<string>localStorage.getItem(GLOBAL_QUICK_SELECT_KEY))
  : false;

export const SIDENAV_FIXED_KEY = "sideNav.fixed";
export const SIDENAV_OPEN_KEY = "sideNav.open";
const initialSideNavFixed: boolean = localStorage.getItem(SIDENAV_FIXED_KEY)
  ? localStorage.getItem(SIDENAV_FIXED_KEY) === "1"
  : false;

const initialSideNavOpen: boolean = localStorage.getItem(SIDENAV_OPEN_KEY)
  ? localStorage.getItem(SIDENAV_OPEN_KEY) === "1"
  : false;

const initialState: UiState = {
  selectedLayerId: null,
  selectedElementToRemove: null,
  headerVisibilityMode: "TOGGLABLE_ON_HOVER",
  footerVisibilityMode: "TOGGLABLE_ON_HOVER",
  selectedMap: null,
  selectedPlot: null,
  selectedTool: null,
  selectedPlotSerieForEditParameter: null,
  selectedLayerForAddParameter: null,
  selectedLayerForEditParameter: null,
  isLayoutArrangementActive: false,
  isQuickSelectActive: initialQuickSelectValue,
  sideNavFixed: initialSideNavFixed,
  sideNavOpen: initialSideNavOpen,
  isInteractionLocked: false,
  aiChatIsRecording: false,
  aiChatIsProcessing: false,
};

// Processes any sort of UI state that is required to control multiple components.
const uiReducer = createSlice({
  name: "ui",
  initialState: initialState,
  reducers: {
    /**
     * Set a currently selected layer. Used to create focusing effects on a particular layer.
     * @param state
     * @param action
     */
    selectLayerById(state, action: PayloadAction<LayerUnion["id"]>) {
      state.selectedLayerId = action.payload;
    },
    /**
     * Keep track of selected item that a user is trying to remove across components. Used for the delete confirmation window.
     * @param state
     * @param action
     */
    selectedElementToRemove(state, action: PayloadAction<RemovableElement>) {
      state.selectedElementToRemove = action.payload;
    },
    /**
     * Set the visibility mode of the header.
     * @param state
     * @param action "ALWAYS_SHOW" to always show the header, and "TOGGLABLE_ON_HOVER" to allow a user to hide/show itby hover.
     */
    setHeaderVisibilityMode(state, action: PayloadAction<UiState["headerVisibilityMode"]>) {
      state.headerVisibilityMode = action.payload;
    },
    /**
     * Set the visibility mode of the footer.
     * @param state
     * @param action "ALWAYS_SHOW" to always show the footer, and "TOGGLABLE_ON_HOVER" to allow a user to hide/show it by hover.
     */
    setFooterVisibilityMode(state, action: PayloadAction<UiState["footerVisibilityMode"]>) {
      state.footerVisibilityMode = action.payload;
    },
    setSelectedMap(state, action: PayloadAction<CartographicMapOutput | null>) {
      state.selectedMap = action.payload;
      state.selectedLayerForEditParameter = null; // clear selecteLaeyerId Selection
    },
    setSelectedPlot(state, action: PayloadAction<Plot | null>) {
      state.selectedPlot = action.payload;
      state.selectedPlotSerieForEditParameter = null; // clear plotSerie Selection
    },
    setSelectedTool(state, action: PayloadAction<Plot | WeatherTableState | TephigramSchemaOutput | null>) {
      state.selectedTool = action.payload;
    },
    setSelectedPlotSerieForEditParameter(state, action: PayloadAction<PlotSeriesType | null>) {
      state.selectedPlotSerieForEditParameter = action.payload;
    },
    setSelectedLayerForEditParameter(state, action: PayloadAction<LayerUnionWithAll | null>) {
      state.selectedLayerForEditParameter = action.payload;
    },
    setSelectedLayerForAddParameter(state, action: PayloadAction<LayerUnionWithAll | null>) {
      state.selectedLayerForAddParameter = action.payload;
    },
    setLayoutArrangement(state, action: PayloadAction<boolean>) {
      state.isLayoutArrangementActive = action.payload;
    },
    setQuickSelectActive(state, action: PayloadAction<boolean>) {
      localStorage.setItem(GLOBAL_QUICK_SELECT_KEY, JSON.stringify(action.payload));
      state.isQuickSelectActive = action.payload;
    },
    setSideNavFixed(state, action: PayloadAction<boolean>) {
      try {
        localStorage.setItem(SIDENAV_FIXED_KEY, action.payload ? "1" : "0");
      } catch {}
      state.sideNavFixed = action.payload;
    },
    toggleSideNavFixed(state) {
      state.sideNavFixed = !state.sideNavFixed;
      try {
        localStorage.setItem(SIDENAV_FIXED_KEY, state.sideNavFixed ? "1" : "0");
      } catch {}
    },
    setSideNavOpen(state, action: PayloadAction<boolean>) {
      state.sideNavOpen = action.payload;
      try {
        localStorage.setItem(SIDENAV_OPEN_KEY, action.payload ? "1" : "0");
      } catch {}
    },
    toggleSideNavOpen(state) {
      state.sideNavOpen = !state.sideNavOpen;
      try {
        localStorage.setItem(SIDENAV_OPEN_KEY, state.sideNavOpen ? "1" : "0");
      } catch {}
    },
    setInteractionLock(state, action: PayloadAction<boolean>) {
      state.isInteractionLocked = action.payload;
    },
    setAiChatRecording(state, action: PayloadAction<boolean>) {
      state.aiChatIsRecording = action.payload;
    },
    setAiChatProcessing(state, action: PayloadAction<boolean>) {
      state.aiChatIsProcessing = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createLayer.fulfilled, (state, action) => {
      state.selectedLayerId = action.payload.id;
    });
  },
});

export const {
  selectLayerById,
  selectedElementToRemove,
  setHeaderVisibilityMode,
  setFooterVisibilityMode,
  setSelectedLayerForEditParameter,
  setSelectedLayerForAddParameter,
  setSelectedMap,
  setSelectedPlot,
  setSelectedTool,
  setSelectedPlotSerieForEditParameter,
  setLayoutArrangement,
  setQuickSelectActive,
  setSideNavFixed,
  toggleSideNavFixed,
  setSideNavOpen,
  toggleSideNavOpen,
  setInteractionLock,
  setAiChatRecording,
  setAiChatProcessing,
} = uiReducer.actions;
export default uiReducer.reducer;
